// src/components/events/PublicEventSection.js
//
// Section « Publication sur le site » des formulaires EventForm / EventEdit :
// visibilité, adresse de la page, image, Instagram, lieu, public visé, date
// limite, traductions, « à prévoir » et éditeur de tickets.
//
// Composant contrôlé : value = état du formulaire (voir defaultPublicForm dans
// utils/publicEvents.js), onChange(patch) fusionne un morceau d'état.

import React, { useState } from 'react';
import {
  Box, Paper, Typography, Switch, Grid, TextField, InputAdornment, Button,
  ToggleButton, ToggleButtonGroup, Tabs, Tab, MenuItem, IconButton, Tooltip,
  LinearProgress, Chip, Alert, Accordion, AccordionSummary, AccordionDetails,
  Divider, Stack, Link,
} from '@mui/material';
import {
  Public as PublicIcon,
  Image as ImageIcon,
  Instagram as InstagramIcon,
  Place as PlaceIcon,
  ConfirmationNumber as TicketIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  ExpandMore as ExpandMoreIcon,
  Translate as TranslateIcon,
  AutoFixHigh as MagicIcon,
  OpenInNew as OpenInNewIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { getApp } from 'firebase/app';
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import {
  LANGS, LANG_LABELS, LEVELS, SITE_BASE, emptyTicket, stockOf, suggestSlug, slugify,
} from '../../utils/publicEvents';

const MAX_IMAGE_MB = 10;

const TICKET_PRESETS = [
  { fr: 'Early bird', en: 'Early bird', nl: 'Early bird' },
  { fr: 'Élève MKD', en: 'MKD student', nl: 'MKD-leerling' },
  { fr: 'Tarif réduit', en: 'Reduced rate', nl: 'Verminderd tarief' },
  { fr: 'Pass journée', en: 'Day pass', nl: 'Dagpas' },
];

// ─── En-tête de section (même style que les formulaires) ──────────────────
function SectionHeader({ icon, title, subtitle, action }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 36, height: 36, borderRadius: '10px',
          backgroundColor: '#EEF2FF', color: '#4338CA', flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2}>{title}</Typography>
        {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
      </Box>
      {action}
    </Box>
  );
}

// ─── Champ texte multilingue (onglets FR / EN / NL) ───────────────────────
function I18nField({ label, value, onChange, multiline = false, required = false, placeholder, maxLength = 500 }) {
  const [lang, setLang] = useState('fr');
  const v = value?.[lang] || '';
  return (
    <Box>
      <Tabs
        value={lang}
        onChange={(_, l) => setLang(l)}
        sx={{ minHeight: 32, '& .MuiTab-root': { minHeight: 32, py: 0.5, px: 1.5, fontSize: '0.8rem' } }}
      >
        {LANGS.map((l) => (
          <Tab
            key={l}
            value={l}
            label={`${l.toUpperCase()}${value?.[l]?.trim() ? ' ✓' : ''}`}
          />
        ))}
      </Tabs>
      <TextField
        fullWidth
        size="small"
        label={`${label} (${LANG_LABELS[lang]})${required && lang === 'fr' ? ' *' : ''}`}
        value={v}
        multiline={multiline}
        minRows={multiline ? 2 : undefined}
        maxRows={multiline ? 6 : undefined}
        placeholder={lang === 'fr' ? placeholder : (value?.fr || placeholder)}
        onChange={(e) => onChange({ ...value, [lang]: e.target.value.slice(0, maxLength) })}
        helperText={lang !== 'fr' && !v ? 'Vide = le texte français est affiché' : ' '}
        sx={{ mt: 1 }}
      />
    </Box>
  );
}

// ─── Éditeur d'un ticket ──────────────────────────────────────────────────
function TicketCard({ ticket, index, count, stock, onChange, onRemove, onMove }) {
  const locked = stock.sold + stock.reserved > 0;
  const qty = ticket.quantity === '' ? null : Number(ticket.quantity);
  const used = stock.sold + stock.reserved;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2, borderRadius: 2,
        borderColor: ticket.active ? '#c7d2fe' : '#e5e7eb',
        backgroundColor: ticket.active ? '#fff' : '#f9fafb',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Chip size="small" icon={<TicketIcon />} label={`Ticket ${index + 1}`} />
        {!ticket.active && <Chip size="small" label="Désactivé" />}
        {locked && (
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={`${stock.sold} vendu${stock.sold > 1 ? 's' : ''}${stock.reserved ? ` · ${stock.reserved} en attente` : ''}`}
          />
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Monter">
          <span><IconButton size="small" disabled={index === 0} onClick={() => onMove(-1)}><UpIcon fontSize="small" /></IconButton></span>
        </Tooltip>
        <Tooltip title="Descendre">
          <span><IconButton size="small" disabled={index === count - 1} onClick={() => onMove(1)}><DownIcon fontSize="small" /></IconButton></span>
        </Tooltip>
        <Tooltip title={locked ? 'Des places ont été vendues : désactivez ce ticket plutôt que de le supprimer' : 'Supprimer ce ticket'}>
          <span>
            <IconButton size="small" color="error" disabled={locked || count === 1} onClick={onRemove}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <I18nField
            label="Nom du ticket"
            required
            value={ticket.name_i18n}
            onChange={(name_i18n) => onChange({ name_i18n })}
            placeholder="Ex : Early bird"
            maxLength={60}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <I18nField
            label="Précision (optionnel)"
            value={ticket.description_i18n}
            onChange={(description_i18n) => onChange({ description_i18n })}
            placeholder="Ex : valable jusqu'au 1er octobre"
            maxLength={140}
          />
        </Grid>

        <Grid item xs={6} md={3}>
          <TextField
            fullWidth size="small" type="number" label="Prix *"
            value={ticket.price}
            onChange={(e) => onChange({ price: e.target.value })}
            InputProps={{
              inputProps: { min: 0, step: 0.5 },
              endAdornment: <InputAdornment position="end">€</InputAdornment>,
            }}
            helperText="0 = gratuit"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            fullWidth size="small" type="number" label="Places"
            value={ticket.quantity}
            onChange={(e) => onChange({ quantity: e.target.value })}
            InputProps={{ inputProps: { min: used, step: 1 } }}
            helperText={qty === null ? 'Vide = illimité' : `${Math.max(qty - used, 0)} restante(s)`}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth size="small" type="datetime-local" label="Fin des ventes (optionnel)"
            value={ticket.salesEnd}
            onChange={(e) => onChange({ salesEnd: e.target.value })}
            InputLabelProps={{ shrink: true }}
            helperText="Ex : fin de l'early bird"
          />
        </Grid>
        <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'flex-start', pt: { md: '20px !important' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Switch checked={ticket.active} onChange={(e) => onChange({ active: e.target.checked })} />
            <Typography variant="body2">{ticket.active ? 'En vente' : 'Masqué'}</Typography>
          </Box>
        </Grid>
        {qty !== null && qty > 0 && (
          <Grid item xs={12}>
            <LinearProgress
              variant="determinate"
              value={Math.min((used / qty) * 100, 100)}
              sx={{ height: 6, borderRadius: 4, backgroundColor: '#eee', '& .MuiLinearProgress-bar': { backgroundColor: '#7C3AED' } }}
            />
          </Grid>
        )}
      </Grid>
    </Paper>
  );
}

// ─── Section complète ─────────────────────────────────────────────────────
export default function PublicEventSection({
  value: pf, onChange, eventId, ticketStock, title, startDate, disabled = false,
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);

  const setTicket = (index, patch) => {
    const ticketTypes = pf.ticketTypes.map((t, i) => (i === index ? { ...t, ...patch } : t));
    onChange({ ticketTypes });
  };
  const addTicket = (name_i18n) => {
    onChange({ ticketTypes: [...pf.ticketTypes, emptyTicket(name_i18n ? { name_i18n: { ...name_i18n } } : {})] });
  };
  const removeTicket = (index) => {
    onChange({ ticketTypes: pf.ticketTypes.filter((_, i) => i !== index) });
  };
  const moveTicket = (index, dir) => {
    const list = [...pf.ticketTypes];
    const j = index + dir;
    if (j < 0 || j >= list.length) return;
    [list[index], list[j]] = [list[j], list[index]];
    onChange({ ticketTypes: list });
  };

  const handleImage = async (file) => {
    setUploadError(null);
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setUploadError('Le fichier doit être une image (JPG, PNG, WebP).');
      return;
    }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      setUploadError(`Image trop lourde (max ${MAX_IMAGE_MB} Mo).`);
      return;
    }
    if (!eventId) {
      setUploadError("Identifiant d'événement manquant, rechargez la page.");
      return;
    }
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '');
    const base = slugify(file.name.replace(/\.[^.]+$/, '')) || 'visuel';
    const path = `event-images/${eventId}/${Date.now()}_${base}.${ext}`;

    try {
      setUploading(true);
      setUploadProgress(0);
      const task = uploadBytesResumable(storageRef(getStorage(getApp()), path), file, { contentType: file.type });
      await new Promise((resolve, reject) => {
        task.on(
          'state_changed',
          (snap) => setUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
          reject,
          resolve,
        );
      });
      const url = await getDownloadURL(task.snapshot.ref);
      onChange({ imageUrl: url, imagePath: path });
    } catch (e) {
      console.error('Upload image événement :', e);
      setUploadError(
        e?.code === 'storage/unauthorized'
          ? "Upload refusé : vérifiez que le compte a bien les droits staff sur Storage."
          : "L'image n'a pas pu être envoyée. Réessayez."
      );
    } finally {
      setUploading(false);
    }
  };

  const pageUrl = pf.slug ? `${SITE_BASE}/evenements/${pf.slug}` : null;
  const translationsCount = [pf.title_en, pf.title_nl, pf.description_en, pf.description_nl].filter((s) => s?.trim()).length;

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 3, border: pf.isPublic ? '2px solid #7C3AED' : undefined }}>
      <SectionHeader
        icon={<PublicIcon />}
        title="Publication sur le site"
        subtitle="Workshops et stages : page Événements de mkdstudio.co avec billetterie en ligne"
      />

      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          p: 2, borderRadius: 2, backgroundColor: '#f8fafc',
        }}
      >
        <Box>
          <Typography fontWeight={600}>Afficher sur le site avec inscription payante</Typography>
          <Typography variant="caption" color="text.secondary">
            Désactivé : événement interne, visible seulement dans l'app et l'admin (comme avant)
          </Typography>
        </Box>
        <Switch
          checked={pf.isPublic}
          disabled={disabled}
          onChange={(e) => {
            const isPublic = e.target.checked;
            const patch = { isPublic };
            if (isPublic && !pf.slug) patch.slug = suggestSlug(title, startDate);
            onChange(patch);
          }}
        />
      </Box>

      {pf.isPublic && (
        <Stack spacing={3} sx={{ mt: 3 }}>
          {/* ── Statut + adresse ─────────────────────────────────────── */}
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={5}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Statut</Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={pf.status}
                onChange={(_, s) => s && onChange({ status: s })}
              >
                <ToggleButton value="draft">Brouillon</ToggleButton>
                <ToggleButton value="published" sx={{ '&.Mui-selected': { backgroundColor: '#e8f5e9', color: '#2e7d32' } }}>
                  En ligne
                </ToggleButton>
                <ToggleButton value="cancelled" sx={{ '&.Mui-selected': { backgroundColor: '#ffebee', color: '#c62828' } }}>
                  Annulé
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                {pf.status === 'draft' && "Invisible sur le site : préparez tout, puis passez « En ligne »."}
                {pf.status === 'published' && 'Visible sur le site, inscriptions ouvertes.'}
                {pf.status === 'cancelled' && 'Affiché « Annulé », ventes fermées. Les remboursements se font à part.'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={7}>
              <TextField
                fullWidth
                size="small"
                label="Adresse de la page *"
                value={pf.slug}
                onChange={(e) => onChange({ slug: slugify(e.target.value), slugTouched: true })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">/evenements/</InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Générer depuis le titre">
                        <IconButton size="small" onClick={() => onChange({ slug: suggestSlug(title, startDate) })}>
                          <MagicIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                helperText="Lettres minuscules, chiffres et tirets. Évitez de la changer une fois partagée."
              />
              {pageUrl && pf.status === 'published' && (
                <Link href={pageUrl} target="_blank" rel="noopener" variant="body2" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  {pageUrl.replace('https://', '')} <OpenInNewIcon sx={{ fontSize: 14 }} />
                </Link>
              )}
            </Grid>
          </Grid>

          <Divider />

          {/* ── Image + Instagram ────────────────────────────────────── */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ImageIcon fontSize="small" color="action" /> Image *
              </Typography>
              <Box
                sx={{
                  position: 'relative', width: '100%', aspectRatio: '16 / 9', borderRadius: 2,
                  border: '1px dashed #cbd5e1', backgroundColor: '#f8fafc', overflow: 'hidden',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundImage: pf.imageUrl ? `url(${pf.imageUrl})` : 'none',
                  backgroundSize: 'cover', backgroundPosition: 'center',
                }}
              >
                {!pf.imageUrl && !uploading && (
                  <Typography variant="caption" color="text.secondary" sx={{ px: 2, textAlign: 'center' }}>
                    Format paysage conseillé (1600 × 900), max {MAX_IMAGE_MB} Mo
                  </Typography>
                )}
              </Box>
              {uploading && <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 1 }} />}
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button component="label" size="small" variant="outlined" startIcon={<UploadIcon />} disabled={uploading || disabled}>
                  {pf.imageUrl ? "Changer l'image" : 'Choisir une image'}
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) => { handleImage(e.target.files?.[0]); e.target.value = ''; }}
                  />
                </Button>
                {pf.imageUrl && (
                  <Button size="small" color="error" onClick={() => onChange({ imageUrl: '', imagePath: '' })} disabled={uploading}>
                    Retirer
                  </Button>
                )}
              </Box>
              {uploadError && <Alert severity="error" sx={{ mt: 1 }}>{uploadError}</Alert>}
            </Grid>

            <Grid item xs={12} md={7}>
              <Stack spacing={2}>
                <TextField
                  fullWidth size="small" label="Lien Instagram (post ou profil)"
                  value={pf.instagramUrl}
                  placeholder="https://www.instagram.com/p/…"
                  onChange={(e) => onChange({ instagramUrl: e.target.value })}
                  InputProps={{ startAdornment: <InputAdornment position="start"><InstagramIcon fontSize="small" color="action" /></InputAdornment> }}
                />
                <TextField
                  fullWidth size="small" label="Nom du lieu"
                  value={pf.locationName}
                  onChange={(e) => onChange({ locationName: e.target.value })}
                  InputProps={{ startAdornment: <InputAdornment position="start"><PlaceIcon fontSize="small" color="action" /></InputAdornment> }}
                />
                <TextField
                  fullWidth size="small" label="Adresse"
                  value={pf.locationAddress}
                  placeholder="Rue, numéro, code postal, ville"
                  onChange={(e) => onChange({ locationAddress: e.target.value })}
                />
              </Stack>
            </Grid>
          </Grid>

          <Divider />

          {/* ── Public visé + inscriptions ───────────────────────────── */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                select fullWidth size="small" label="Niveau"
                value={pf.level}
                onChange={(e) => onChange({ level: e.target.value })}
              >
                {LEVELS.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth size="small" type="number" label="Âge min"
                value={pf.ageMin}
                onChange={(e) => onChange({ ageMin: e.target.value })}
                InputProps={{ inputProps: { min: 0, max: 99 } }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth size="small" type="number" label="Âge max"
                value={pf.ageMax}
                onChange={(e) => onChange({ ageMax: e.target.value })}
                InputProps={{ inputProps: { min: 0, max: 99 } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth size="small" type="datetime-local" label="Date limite d'inscription"
                value={pf.registrationDeadline}
                onChange={(e) => onChange({ registrationDeadline: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Vide = jusqu'au début de l'événement"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                L'âge est vérifié à l'inscription, à la date de l'événement. Laissez vide pour « tous âges ».
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <I18nField
                label="À prévoir"
                multiline
                value={pf.whatToBring}
                onChange={(whatToBring) => onChange({ whatToBring })}
                placeholder="Ex : tenue confortable, baskets propres, bouteille d'eau"
              />
            </Grid>
          </Grid>

          {/* ── Traductions titre / description ──────────────────────── */}
          <Accordion disableGutters variant="outlined" sx={{ borderRadius: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TranslateIcon fontSize="small" color="action" />
                <Typography fontWeight={600}>Titre et description en anglais / néerlandais</Typography>
                <Chip size="small" label={`${translationsCount}/4`} />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Le titre et la description en français sont ceux de la section « Informations générales ».
                Vide = le français est affiché.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth size="small" label="Titre (English)" value={pf.title_en}
                    onChange={(e) => onChange({ title_en: e.target.value })} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth size="small" label="Titel (Nederlands)" value={pf.title_nl}
                    onChange={(e) => onChange({ title_nl: e.target.value })} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth size="small" multiline minRows={3} maxRows={10} label="Description (English)"
                    value={pf.description_en}
                    onChange={(e) => onChange({ description_en: e.target.value.slice(0, 3000) })} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth size="small" multiline minRows={3} maxRows={10} label="Beschrijving (Nederlands)"
                    value={pf.description_nl}
                    onChange={(e) => onChange({ description_nl: e.target.value.slice(0, 3000) })} />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Divider />

          {/* ── Tickets ──────────────────────────────────────────────── */}
          <Box>
            <SectionHeader
              icon={<TicketIcon />}
              title="Tickets et prix"
              subtitle="Chaque ticket a son prix et son nombre de places. « Complet » s'affiche automatiquement."
            />
            <Stack spacing={2}>
              {pf.ticketTypes.map((t, i) => (
                <TicketCard
                  key={t.id}
                  ticket={t}
                  index={i}
                  count={pf.ticketTypes.length}
                  stock={stockOf(ticketStock, t.id)}
                  onChange={(patch) => setTicket(i, patch)}
                  onRemove={() => removeTicket(i)}
                  onMove={(dir) => moveTicket(i, dir)}
                />
              ))}
            </Stack>
            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => addTicket()}>
                Ajouter un ticket
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>Raccourcis :</Typography>
              {TICKET_PRESETS.map((p) => (
                <Chip key={p.fr} label={p.fr} size="small" variant="outlined" onClick={() => addTicket(p)} />
              ))}
            </Box>
          </Box>
        </Stack>
      )}
    </Paper>
  );
}
